// The banner across the journal in the demo, and the prompt shown when a demo
// visitor tries something only a real account can do (import, add trades, settings).
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { dayKey } from './pnl';
import { useI18n } from './i18n';
import { trackStep } from './analytics';

export function DemoBanner({ user, onSignup, onLogin }) {
  const { t, tr } = useI18n();
  const demo = user.demo || {};
  // The story links show the Topstep account; "Honest take" looks across all three.
  const topstep = demo.accountId ? { selectAccounts: demo.accountId } : undefined;
  const links = [
    { to: '/app/dashboard', label: t('The rules panel'), state: topstep },
    { to: '/app/insights', label: t('The costly pattern'), state: topstep },
    { to: '/app/insights', label: t('Honest take'), state: { selectAccounts: 'all' }, id: 'honest' },
    demo.tiltDate && { to: `/app/reflection?date=${demo.tiltDate}`, label: t('The tilt day') },
    { to: '/app/session', label: demo.lastDate === dayKey(new Date()) ? t('Today’s reset') : t('A sample session') },
  ].filter(Boolean);

  return (
    <section className="demo-banner" aria-label={t('Demo')}>
      <div className="demo-banner-text">
        <p>
          {tr('<0>You\u2019re in the demo:</0> a sample trader on day 8 of a Topstep 50K evaluation, with gold on Apex and crude on MyFundedFutures, one tilt day that spread to two accounts and a consistency rule over its limit. Your own account isn\u2019t affected. Your trades tell a different story.',
            [(x) => <strong>{x}</strong>])}
        </p>
        <nav className="demo-banner-links" aria-label={t('Explore the demo')}>
          <span>{t('See:')}</span>
          {links.map((l) => <Link key={l.id || l.to} to={l.to} state={l.state} onClick={() => trackStep('demoExplore')}>{l.label}</Link>)}
        </nav>
      </div>
      <div className="demo-banner-actions">
        <button type="button" onClick={() => { trackStep('demoSignup'); onSignup(); }}>{t('Start your free journal')} &rarr;</button>
        {onLogin && <button type="button" className="link-button" onClick={onLogin}>{t('Log in to my account')}</button>}
      </div>
    </section>
  );
}

export function DemoGate({ onSignup }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener('mm-demo-gate', show);
    return () => window.removeEventListener('mm-demo-gate', show);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && setOpen(false)}>
      <div className="modal demo-gate" role="dialog" aria-modal="true" aria-labelledby="demo-gate-title">
        <div className="modal-header">
          <h2 id="demo-gate-title">{t('This is a sample account')}</h2>
          <button type="button" className="modal-close" onClick={() => setOpen(false)} aria-label={t('Close')}>&times;</button>
        </div>
        <p>{t('Create your free account to import your own trades and keep your journal. Your rules panel, insights and routine work the same way, with your numbers.')}</p>
        <p className="cta-note">{t('Every feature free for 14 days, no credit card. The core journal stays free.')}</p>
        <div className="modal-actions">
          <button type="button" onClick={() => { trackStep('demoSignup'); onSignup(); }}>{t('Start your free journal')} &rarr;</button>
          <button type="button" className="secondary" onClick={() => setOpen(false)}>{t('Keep exploring')}</button>
        </div>
      </div>
    </div>
  );
}
